/**
 * Per-tutorial *environment* visibility: where in the app a tutorial surfaces.
 *
 * This is orthogonal to *role* visibility (who may see it), which lives in
 * `articleVisibility.ts` (`ArticleVisibility` / `canViewArticle`). The app combines
 * both gates; the knowledge base applies only the role gate (environment is an
 * app-only concern).
 *
 *   - "production"  — app, production builds only.
 *   - "development" — app, dev / staging builds only (the default).
 *   - "both"        — app, every environment.
 *   - "nowhere"     — never surfaced in the app. Still editable in the knowledge base.
 *
 * The value is authored per tutorial in its YAML (`environmentVisibility:`) and edited
 * through the tutorial editor. A missing value falls back to the default below.
 */

export type EnvironmentVisibility =
  | "production"
  | "development"
  | "both"
  | "nowhere";

/** The runtime environment the app is built/served for. Staging counts as development. */
export type AppEnvironment = "production" | "development";

export const DEFAULT_ENVIRONMENT_VISIBILITY: EnvironmentVisibility =
  "development";

/** Selectable options, in the order shown in the editor dropdown. */
export const environmentVisibilityOptions: EnvironmentVisibility[] = [
  "both",
  "production",
  "development",
  "nowhere",
];

/** The effective environment visibility of a tutorial (missing → the default). */
export const resolveEnvironmentVisibility = (tutorial: {
  environmentVisibility?: EnvironmentVisibility;
}): EnvironmentVisibility =>
  tutorial.environmentVisibility ?? DEFAULT_ENVIRONMENT_VISIBILITY;

/** True when a tutorial with `setting` should appear in the app for `environment`. */
export const isVisibleInAppEnvironment = (
  setting: EnvironmentVisibility,
  environment: AppEnvironment,
): boolean => {
  switch (setting) {
    case "both":
      return true;
    case "nowhere":
      return false;
    case "production":
      return environment === "production";
    case "development":
      return environment === "development";
  }
};

/**
 * Whether a tutorial's *environment* setting permits it in the app for `environment`.
 * Callers combine this with the role gate (`canViewArticle`) to decide availability.
 */
export const isTutorialInAppEnvironment = (
  tutorial: { environmentVisibility?: EnvironmentVisibility },
  environment: AppEnvironment,
): boolean =>
  isVisibleInAppEnvironment(resolveEnvironmentVisibility(tutorial), environment);
